import { useCallback, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Space,
  Tooltip,
} from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { sendWebSocketMessage } from "../../../../store/chats/slice";
import { createMessagePayload } from "./helper";
import httpClient from "../../../../api/httpClient";
import dayjs from "../../../../utils/dayjs";
import { getFullName, parseError, PLAN_TYPES } from "../../../../utils/shared";
import Modal from "../../../../components/generic/Modal";
import { JOIN_MEETING_TOKEN } from "../../../../constant";
import MemberSelect from "../../../../components/shared/MemberSelect";
import { selectSelectedTenant } from "../../../../store/user/selectors";
const CreateMeetingModal = ({ selectedChat, user, md = false }) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const selectedTenant = useSelector(selectSelectedTenant);

  const dispatch = useDispatch();

  const onFinish = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        //@ts-ignore
        const { data, note } = await httpClient.post("appointments", {
          ...values,
          start_time: dayjs(values?.start_time).toISOString(),
          conversation_id: selectedChat?.id,
          tenant_id: selectedChat?.tenant_id,
          customer_ids: values?.customer_ids || [],
        });

        if (note) {
          notification.warning({
            title: "Upgrade Required",
            description: note,
          });
        }

        const meetingMessage = [
          `📅 ${data?.title}\n`,
          ` 👤 Host: ${getFullName(user)}`,
          ...(data?.description?.trim()
            ? [` 📋 Description: ${data?.description}`]
            : ""),
          `\n 🕐 Start Time: ${dayjs(data?.start_time).utc().format()}`,
          ` ⏱️ Duration: ${data?.call_duration_minutes} minutes\n`,
          ` 🏠 Room Code: ${data?.room_code}`,
          ` 🔑 Room Password: ${data?.room_password}`,
          JOIN_MEETING_TOKEN,
        ].join("\n");

        await dispatch(
          sendWebSocketMessage(
            createMessagePayload({
              message: meetingMessage,
              selectedChat,
              user: {},
              is_bot: true,
            }),
          ),
        );
        setOpen(false);
        form.resetFields();
      } catch (error) {
        parseError(error, true);
      } finally {
        setSubmitting(false);
      }
    },
    [selectedChat, user],
  );

  const openModal = useCallback(() => {
    if (selectedTenant?.plan_details?.key === PLAN_TYPES.STARTER) {
      notification.warning({
        title: "Upgrade Required",
        description:
          "Appointment scheduling is not available on the Starter plan. Please upgrade your plan to access this feature.",
      });
      return;
    }

    setOpen(true);
    form.resetFields();
  }, [selectedTenant]);

  return (
    <>
      {md ? (
        <span onClick={openModal}>
          <VideoCameraOutlined style={{ marginRight: 8 }} />
          Create Meeting
        </span>
      ) : (
        <Tooltip title="Create Meeting">
          <Button
            onClick={openModal}
            type="text"
            icon={<VideoCameraOutlined />}
            className="action-btn"
          />
        </Tooltip>
      )}
      <Modal
        title={
          <Space>
            <VideoCameraOutlined />
            Create Meeting
          </Space>
        }
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={480}
        isProcessing={submitting}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            call_duration_minutes: 15,
            advisor_ids: selectedChat?.members?.map(({ id }) => id),
            customer_ids: selectedChat?.customers?.map(({ id }) => id),
            start_time: dayjs(),
          }}
          disabled={submitting}
          onFinish={onFinish}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Please enter a meeting title" },
            ]}
          >
            <Input placeholder="Enter meeting title" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea
              rows={3}
              placeholder="Enter meeting description"
              showCount
              maxLength={300}
            />
          </Form.Item>

          <Form.Item
            label="Duration (minutes)"
            name="call_duration_minutes"
            rules={[
              { required: true, message: "Please enter the call duration" },
              {
                type: "number",
                min: 1,
                max: 480,
                message: "Duration must be between 1 and 480 minutes",
              },
            ]}
          >
            <InputNumber
              min={1}
              max={480}
              style={{ width: "100%" }}
              placeholder="e.g. 15"
              addonAfter="min"
            />
          </Form.Item>

          <Form.Item
            label="Start Time"
            name="start_time"
            rules={[{ required: true, message: "Please select a start time" }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: "100%" }}
              placeholder="Select date and time"
            />
          </Form.Item>

          {selectedChat?.customer && (
            <Form.Item label="Customers" name="customer_ids">
              <MemberSelect
                users={selectedChat?.customers}
                mode="multiple"
                placeholder="Select Customers"
              />
            </Form.Item>
          )}
          <Form.Item
            label="Advisors"
            name="advisor_ids"
            rules={[
              { required: true, message: "Please select at least one advisor" },
            ]}
          >
            <MemberSelect
              users={selectedChat?.members}
              mode="multiple"
              placeholder="Select Advisors"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateMeetingModal;
